import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { ShieldCheck, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function IHResponse() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [ctx, setCtx] = useState(undefined);
  const [hasScholarship, setHasScholarship] = useState(true);
  const [percentage, setPercentage] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) { setCtx(null); return; }
    api.get(`/ih-response/${token}`).then(({ data }) => setCtx(data))
      .catch((err) => { setCtx(null); toast.error(formatApiError(err.response?.data?.detail)); });
  }, [token]);

  const submit = async () => {
    if (hasScholarship && (percentage === "" || Number(percentage) < 0 || Number(percentage) > 100)) {
      toast.error("Ingresa un porcentaje válido (0-100)."); return;
    }
    setBusy(true);
    try {
      await api.post(`/ih-response/${token}`, {
        has_scholarship: hasScholarship,
        percentage: hasScholarship ? Number(percentage) : 0,
        note,
      });
      setDone(true);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-5 py-12">
      <div className="w-full max-w-md fade-in">
        <div className="flex items-center gap-2 mb-8 justify-center">
          <ShieldCheck className="w-7 h-7 text-primary" />
          <span className="font-heading font-extrabold text-xl text-primary">EIM Becas · Instituto Hebreo</span>
        </div>
        <div className="bg-white rounded-2xl border border-border p-8">
          {ctx === undefined ? (
            <div className="flex justify-center py-8"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
          ) : ctx === null ? (
            <div className="text-center space-y-3">
              <AlertTriangle className="w-10 h-10 text-amber-600 mx-auto" />
              <h1 className="text-xl font-bold">Enlace no válido</h1>
              <p className="text-muted-foreground text-sm">Este enlace es inválido, ya fue utilizado o expiró.</p>
            </div>
          ) : done ? (
            <div className="text-center space-y-3">
              <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto" />
              <h1 className="text-xl font-bold">¡Gracias!</h1>
              <p className="text-muted-foreground">Tu respuesta fue registrada de forma segura.</p>
            </div>
          ) : (
            <div className="space-y-5">
              <h1 className="text-2xl font-bold">Consulta de beca</h1>
              <p className="text-sm text-muted-foreground">
                Solicitud <strong>{ctx.human_id}</strong> — familia <strong>{ctx.family_name}</strong>.
                {ctx.children?.length > 0 && <> Alumnos/as: {ctx.children.map((c) => c.nombre).join(", ")}.</>}
              </p>
              <div className="flex items-center justify-between bg-secondary/50 rounded-xl p-4">
                <span className="font-semibold text-sm">¿La familia cuenta actualmente con beca IH?</span>
                <Switch checked={hasScholarship} onCheckedChange={setHasScholarship} data-testid="ih-has-scholarship" />
              </div>
              {hasScholarship && (
                <div>
                  <Label className="text-sm font-semibold uppercase tracking-wide text-slate-600">Porcentaje de beca (%)</Label>
                  <Input type="number" className="h-14 text-lg rounded-xl mt-2" value={percentage}
                    onChange={(e) => setPercentage(e.target.value)} data-testid="ih-percentage" />
                </div>
              )}
              <div>
                <Label className="text-sm font-semibold uppercase tracking-wide text-slate-600">Comentario (opcional)</Label>
                <Textarea className="rounded-xl mt-2" value={note} onChange={(e) => setNote(e.target.value)} />
              </div>
              <Button onClick={submit} disabled={busy} className="btn-lift w-full h-14 text-lg rounded-xl" data-testid="btn-ih-submit">
                {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : "Enviar respuesta"}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
